import React, { useEffect, useState, useCallback } from 'react';
import client from '../api/client';
import toast from 'react-hot-toast';

const ZODIAC_SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
];

const ZODIAC_SYMBOLS = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋',
  Leo: '♌', Virgo: '♍', Libra: '♎', Scorpio: '♏',
  Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
};

const ZODIAC_TURKISH = {
  Aries: 'Koç', Taurus: 'Boğa', Gemini: 'İkizler', Cancer: 'Yengeç',
  Leo: 'Aslan', Virgo: 'Başak', Libra: 'Terazi', Scorpio: 'Akrep',
  Sagittarius: 'Yay', Capricorn: 'Oğlak', Aquarius: 'Kova', Pisces: 'Balık',
};

const LANGS = [
  { code: 'en', label: '🇬🇧 EN' },
  { code: 'tr', label: '🇹🇷 TR' },
  { code: 'de', label: '🇩🇪 DE' },
];

function ScoreBar({ label, score, max = 10, bgColor }) {
  const pct = Math.round((score / max) * 100);
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs text-slate-400 mb-1">
        <span>{label}</span>
        <span>{score}/{max}</span>
      </div>
      <div className="w-full bg-slate-700 rounded-full h-2">
        <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: bgColor }} />
      </div>
    </div>
  );
}

function getToday() {
  const d = new Date();
  return d.toISOString().split('T')[0];
}

export default function ZodiacBrowser() {
  const [selectedSign, setSelectedSign] = useState('Leo');
  const [selectedLang, setSelectedLang] = useState('tr');
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [signsData, setSignsData] = useState([]);

  const fetchSigns = useCallback(async () => {
    try {
      const res = await client.get('/api/v1/admin/predictions/signs');
      setSignsData(res.data.signs || []);
    } catch {
      // silently fail
    }
  }, []);

  const fetchPrediction = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(
        `/api/v1/admin/predictions/daily/${selectedSign}/${selectedDate}?lang=${selectedLang}`
      );
      setPrediction(res.data);
    } catch (err) {
      toast.error('Yorum yüklenemedi');
      setPrediction(null);
    } finally {
      setLoading(false);
    }
  }, [selectedSign, selectedLang, selectedDate]);

  useEffect(() => { fetchSigns(); }, [fetchSigns]);
  useEffect(() => { fetchPrediction(); }, [fetchPrediction]);

  const pred = prediction?.prediction;
  const wm = prediction?.weekly_monthly;

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">🔮 Burç Tarayıcı</h2>

      {/* Sign Selector */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4 mb-6">
        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
          {ZODIAC_SIGNS.map(sign => {
            const sd = signsData.find(s => s.sign === sign);
            const hasTodayCache = sd?.today_cache && Object.keys(sd.today_cache).length > 0;
            return (
              <button
                key={sign}
                onClick={() => setSelectedSign(sign)}
                className={`flex flex-col items-center p-2 rounded-lg transition text-xs ${
                  selectedSign === sign
                    ? 'bg-indigo-500 text-white ring-2 ring-indigo-400'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <span className="text-lg">{ZODIAC_SYMBOLS[sign]}</span>
                <span className="mt-1 font-medium">{ZODIAC_TURKISH[sign]}</span>
                {hasTodayCache && (
                  <span className="mt-0.5 text-[10px] text-green-400">●</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        {/* Language */}
        <div className="flex gap-1 bg-slate-800 rounded-lg border border-slate-700 p-1">
          {LANGS.map(l => (
            <button
              key={l.code}
              onClick={() => setSelectedLang(l.code)}
              className={`px-3 py-1.5 rounded text-sm transition ${
                selectedLang === l.code
                  ? 'bg-indigo-500 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        {/* Date */}
        <input
          type="date"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
        />
      </div>

      {/* Result */}
      {loading ? (
        <div className="bg-slate-800/50 rounded-lg border border-slate-700/50 p-12 text-center">
          <p className="text-slate-400">Yükleniyor...</p>
        </div>
      ) : !prediction?.found ? (
        <div className="bg-slate-800/50 rounded-lg border border-dashed border-slate-600 p-12 text-center">
          <p className="text-4xl mb-3">{ZODIAC_SYMBOLS[selectedSign]}</p>
          <p className="text-slate-400 text-lg">Cache bulunamadı</p>
          <p className="text-slate-600 text-sm mt-1">
            {ZODIAC_TURKISH[selectedSign]} · {selectedDate} · {selectedLang.toUpperCase()}
          </p>
          <p className="text-slate-600 text-xs mt-3">
            Bu burç+tarih+dil kombinasyonu için henüz yorum üretilmemiş.
          </p>
        </div>
      ) : (
        <div>
          {/* Status Badge */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xl">{ZODIAC_SYMBOLS[selectedSign]}</span>
            <span className="text-white font-semibold text-lg">
              {ZODIAC_TURKISH[selectedSign]} — {selectedDate}
            </span>
            <span className="px-2 py-0.5 bg-green-900/40 text-green-400 text-xs rounded">Cache HIT ✓</span>
            {prediction.created_at && (
              <span className="text-xs text-slate-500">
                Üretilme: {new Date(prediction.created_at).toLocaleString('tr-TR')}
              </span>
            )}
          </div>

          {/* Prediction Content */}
          {pred && (
            <div className="bg-slate-800 rounded-lg border border-slate-700 p-5 mb-4">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">📋 Günlük Yorum</h3>
              {pred.general_prediction && (
                <p className="text-slate-300 text-sm mb-4 leading-relaxed">{pred.general_prediction}</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {pred.love && (
                  <div className="bg-slate-700/50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-pink-400 mb-1">❤️ Aşk</p>
                    <p className="text-xs text-slate-300">{pred.love}</p>
                  </div>
                )}
                {pred.career && (
                  <div className="bg-slate-700/50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-indigo-400 mb-1">💼 Kariyer</p>
                    <p className="text-xs text-slate-300">{pred.career}</p>
                  </div>
                )}
                {pred.health && (
                  <div className="bg-slate-700/50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-green-400 mb-1">🌱 Sağlık</p>
                    <p className="text-xs text-slate-300">{pred.health}</p>
                  </div>
                )}
              </div>
              {pred.advice && (
                <p className="text-sm text-yellow-400/80 pt-3 border-t border-slate-700">💡 {pred.advice}</p>
              )}
              {(pred.love_score || pred.career_score || pred.luck_score) && (
                <div className="mt-4 pt-3 border-t border-slate-700">
                  <ScoreBar label="Aşk" score={pred.love_score || 0} bgColor="#ec4899" />
                  <ScoreBar label="Kariyer" score={pred.career_score || 0} bgColor="#6366f1" />
                  <ScoreBar label="Şans" score={pred.luck_score || 0} bgColor="#eab308" />
                </div>
              )}
            </div>
          )}

          {/* Weekly/Monthly */}
          {wm && (
            <div className="bg-slate-800 rounded-lg border border-slate-700 p-5">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">📊 Haftalık & Aylık</h3>
              {wm.weekly_summary && (
                <div className="mb-4">
                  <p className="text-xs text-slate-500 mb-1">Haftalık Özet</p>
                  <p className="text-sm text-slate-300">{wm.weekly_summary}</p>
                </div>
              )}
              {wm.weekly_highlights && (
                <div className="mb-4 space-y-1">
                  {wm.weekly_highlights.map((h, i) => (
                    <p key={i} className="text-xs text-slate-400">• {h}</p>
                  ))}
                </div>
              )}
              {wm.monthly_summary && (
                <div className="mb-4 pt-3 border-t border-slate-700">
                  <p className="text-xs text-slate-500 mb-1">Aylık Özet</p>
                  <p className="text-sm text-slate-300">{wm.monthly_summary}</p>
                </div>
              )}
              {(wm.monthly_career || wm.monthly_love || wm.monthly_health || wm.monthly_finance) && (
                <div className="pt-3 border-t border-slate-700">
                  <ScoreBar label="Kariyer" score={wm.monthly_career || 0} max={5} bgColor="#6366f1" />
                  <ScoreBar label="Aşk" score={wm.monthly_love || 0} max={5} bgColor="#ec4899" />
                  <ScoreBar label="Sağlık" score={wm.monthly_health || 0} max={5} bgColor="#22c55e" />
                  <ScoreBar label="Finans" score={wm.monthly_finance || 0} max={5} bgColor="#eab308" />
                </div>
              )}
            </div>
          )}

          {/* Raw JSON fallback */}
          {!pred && (
            <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">📦 Ham Cache Verisi</h3>
              <pre className="text-xs text-slate-400 whitespace-pre-wrap max-h-96 overflow-y-auto">
                {JSON.stringify(prediction, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
